import { useFeature } from "@growthbook/growthbook-react";
import clsx from "clsx";
import { useQueryState } from "nuqs";
import { Suspense, useEffect, useImperativeHandle, useRef } from "react";
import { Redirect, Route, Switch, useRouteMatch } from "react-router-dom";

import {
  Loader,
  NotificationStack,
  NotificationType,
} from "@transfr-inc/dashboard-components";
import { useContainerQuery } from "@transfr-inc/dashboard-components/container-query";

import { ClientServicesHeader } from "@components/client-services-header";
import {
  ContainerRoute,
  DownloadResults,
  SupportChat,
  TrackingServiceWrapper,
} from "@components/common";
import { RequestProgressModal } from "@components/modals";
import { NavigationBar } from "@components/navbar";
import { PreviewModeHeader } from "@components/preview-mode-header";
import {
  GROWTHBOOK_TRACKER_ID,
  PRIMARY_TRACKER_ID,
} from "@config/tracking-config";
import { useNotificationContext } from "@contexts/notification-context";
import { ProgramsRoute, Routes, StudentRoutes } from "@lib/routes";
import { previewParser } from "@lib/utils/search-params-parsers";
import { hasRole, Role } from "@models/role";
import {
  useCurrentUser,
  useOrganizationProgramsEnabled,
  usePreviewClassroomModeEnabled,
  useRole,
} from "@queries";
import { useAppStore } from "@src/stores/zustand/app-store";
import { useOutcomesUIStore } from "@src/stores/zustand/outcomes-ui.store";
import { CsvExportView } from "@views/outcomes/exports/csv-export.view";
import { OutcomesRoutes } from "@views/outcomes/outcomes.routes";
import { default as RedirectPage } from "@views/redirect";

import Panel from "./panel";

import "./app.scss";

const query = {
  "small-panel": {
    minWidth: 320,
    maxWidth: 767,
  },
  "medium-panel": {
    minWidth: 768,
    maxWidth: 1024,
  },
  "large-panel": {
    minWidth: 1024,
  },
};

export default function App() {
  const { path } = useRouteMatch();
  const role = useRole();
  const currentUser = useCurrentUser();
  const redirect = useAppStore((state) => state.redirect);

  const outcomeReportingEnabled = useFeature("outcome-reporting.enabled").on;

  // The raw-data export a scope view has requested; consumed by the app-root
  // export controller below.
  const exportRequest = useOutcomesUIStore((state) => state.exportRequest);

  const isStudent = role == Role.STUDENT.id;

  // Drop the route only once the flag has answered, or a reload redirects away.
  const programsFlag = useOrganizationProgramsEnabled({
    enabled: !!hasRole(ProgramsRoute.roles, role),
  });
  const programsRouteOff = programsFlag.isSuccess
    ? programsFlag.data !== true
    : programsFlag.isError;
  const applicationRoutes = (isStudent ? StudentRoutes : Routes).filter(
    (route) => !programsRouteOff || route.path !== ProgramsRoute.path
  );
  const { path: defaultPath } = applicationRoutes[0];

  const impersonationMode = currentUser?.impersonation;
  const organizationCode = currentUser?.organizationCode;

  const isPreviewEnabled = usePreviewClassroomModeEnabled();
  const storedPreviewId = useAppStore((state) => state.previewClassroomId);
  const [previewClassroomId] = useQueryState("preview", previewParser);
  const previewMode =
    (!!previewClassroomId || !!storedPreviewId) && isPreviewEnabled;

  const [params, containerRef] = useContainerQuery(query);

  const panelRef = useRef();
  useImperativeHandle(containerRef, () => panelRef.current);

  const {
    notifications,
    addNotification,
    closeNotification,
    closeNotificationTimeout,
  } = useNotificationContext();

  const onSkipNavigation = (e) => {
    e.preventDefault();
    panelRef.current.focus();
  };

  useEffect(() => {
    if (currentUser) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        email: currentUser.email,
        orgCode: organizationCode,
        orgName: currentUser.organizationName,
      });
    }
  }, [currentUser, organizationCode]);

  useEffect(() => {
    if (redirect?.error) {
      const notificationId = addNotification({
        type: NotificationType.error,
        message: "An error occurred, please try again.",
      });

      return closeNotificationTimeout(notificationId);
    }
  }, [redirect]);

  return (
    <div className={clsx("app-layout", impersonationMode && "impersonate")}>
      {impersonationMode && (
        <ClientServicesHeader role={role} currentUser={currentUser} />
      )}
      {previewMode && (
        <PreviewModeHeader role={role} currentUser={currentUser} />
      )}
      <div className="app-layout-container">
        <TrackingServiceWrapper
          trackerId={PRIMARY_TRACKER_ID}
          collectorUrl={process.env.SNOWPLOW_COLLECTOR_URL}
          currentUser={currentUser}
          growthbookTrackerId={GROWTHBOOK_TRACKER_ID}
          growthbookCollectorUrl={process.env.SNOWPLOW_GROWTHBOOK_COLLECTOR_URL}
        >
          <NavigationBar
            addNotification={addNotification}
            onSkipNavigation={onSkipNavigation}
          />
          <Panel ref={panelRef} classNames={clsx("route-container", params)}>
            <Suspense fallback={<Loader fixed />}>
              <Switch>
                {outcomeReportingEnabled && !isStudent && (
                  <Route path={`${path}/dashboard`}>
                    <OutcomesRoutes basePath={path} />
                  </Route>
                )}
                {applicationRoutes.map(
                  (route, index) =>
                    !route.isExternalLink &&
                    (hasRole(route.roles, role) ||
                      (isPreviewEnabled &&
                        hasRole(route.previewRoles, role))) && (
                      <ContainerRoute
                        key={index}
                        path={`${path}${route.path}`}
                        exact={route.exact}
                        component={route.component}
                        responsiveStyles={params}
                      ></ContainerRoute>
                    )
                )}
                <Route path={`${path}/redirect`}>
                  <RedirectPage />
                </Route>
                <Route path="*">
                  <Redirect to={`${path}${defaultPath}`} />
                </Route>
              </Switch>
            </Suspense>
          </Panel>

          <NotificationStack
            className={"app-notification-stack"}
            notifications={notifications}
            onClose={(id) => closeNotification({ id })}
            closable
            animated
          />
          <DownloadResults></DownloadResults>
          <RequestProgressModal />

          {/* Mounted at the app root so an in-flight raw-data export keeps
              polling and toasting as the viewer navigates between pages, and
              resumes a job left in flight after a refresh. */}
          {outcomeReportingEnabled && !isStudent && (
            <CsvExportView request={exportRequest} />
          )}

          {!isStudent && currentUser && <SupportChat user={currentUser} />}
        </TrackingServiceWrapper>
      </div>
    </div>
  );
}
